import styled from 'styled-components'
import { type MultiStoryData } from '@/features/cms/components/CMS/types'
import { Body, Headline } from '@shipt/design-system-typography'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { spacing } from '@/features/shared/theme/tokens'
import { calcRem, screenSizes } from '@shipt/design-system-themes'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { Carousel } from '@/features/shop/components/Carousel'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { MultiStoryCards } from '@/features/cms/components/CMS/components/MultiStoryCards'
import { MultiStoryRefreshCards } from '@/features/cms/components/CMS/components/MultiStoryRefreshCards'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `multistory` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/multistory
 * - **Production** - https://cms.shipt.com/content-type/pool/multistory
 */
export const MultiStory = ({
  content_type_id,
  id,
  data: {
    heading,
    subheading_rich_text,
    cta_link,
    story_subsections,
    disclaimer_rich_text,
    background_color,
    carousel_multistory_cards = false,
    text_color,
    heading_alignment = 'center',
    style_variant,
    desktop_columns,
  },
}: MultiStoryData) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const hasCTA = story_subsections.some((story) => story.data.cta_link)
  const isRefresh = style_variant === 'refresh'
  const { screenSm } = useMediaQuery()
  const CardContainer = isRefresh ? RefreshContainer : Container
  const MultiStoryCardItems = isRefresh ? MultiStoryRefreshCards : MultiStoryCards

  const getCards = (isCarousel: boolean) => (
    <MultiStoryCardItems
      stories={story_subsections}
      text_color={text_color ?? ''}
      isCarousel={isCarousel}
      hasCTA={hasCTA}
    />
  )

  try {
    return (
      <ContentContainer
        $background_color={background_color}
        $text_color={text_color}
        className={background_color ? 'with-background ' : undefined}
        {...getCMSWrapperProps({ content_type_id, id })}
        contentMaxWidth
      >
        <Column margin={{ sm: [0, 'auto'] }}>
          {!!heading && (
            <HeadingContainer margin={['md', 'auto', 'xl']} spacing="sm">
              <Headline as="h2" size="xl" align={heading_alignment}>
                {heading}
              </Headline>
              {!!subheading_rich_text && (
                <Body as="div" align="center">
                  {subheading_rich_text.elements.map(renderElement)}
                </Body>
              )}
            </HeadingContainer>
          )}
          {/* This displays if the screen is desktop size or carousel is not enabled */}
          {!!carousel_multistory_cards && !screenSm && (
            <CardContainer
              display={{
                base: 'none',
                md: 'flex',
              }}
              $itemCount={story_subsections.length}
              $desktopColumns={desktop_columns}
              // only needed for original Container, not RefreshContainer
              data-has-desktop-columns={Boolean(desktop_columns)}
            >
              {getCards(false)}
            </CardContainer>
          )}
          {!carousel_multistory_cards && (
            <CardContainer
              display={{
                base: 'none',
                md: 'flex',
              }}
              direction={{
                base: 'column',
                md: undefined,
              }}
              $itemCount={story_subsections.length}
              $desktopColumns={desktop_columns}
              // only needed for original Container, not RefreshContainer
              data-has-desktop-columns={Boolean(desktop_columns)}
            >
              {getCards(false)}
            </CardContainer>
          )}
          {/* This displays on small screens if carousel is enabled */}
          <CarouselWrapper display={{ base: 'block', md: 'none' }}>
            {!!carousel_multistory_cards && (
              <Carousel
                freeMode
                simulateTouch
                pagination={{ el: '.swiper-pagination', clickable: true }}
                slidesOffsetBefore={16}
                slidesOffsetAfter={16}
              >
                {getCards(true)}
              </Carousel>
            )}
          </CarouselWrapper>
          {!!cta_link && (
            <MultiStoryLink
              href={cta_link.url}
              onClick={() => {
                trackCMSElementClicked({
                  content: 'get_started_default',
                  type: 'link',
                })
              }}
              opensInNewTab={cta_link.open_in_new_tab}
            >
              {cta_link.text}{' '}
            </MultiStoryLink>
          )}
          {!!disclaimer_rich_text && (
            <DisclaimerTextContainer margin={[0, 'auto']} marginTop="xl">
              {disclaimer_rich_text.elements.map(renderElement)}
            </DisclaimerTextContainer>
          )}
        </Column>
      </ContentContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const ContentContainer = styled(GridLayoutContainer)<{
  $background_color?: string | null
  $text_color?: string | null
}>`
  background-color: ${({ $background_color }) => $background_color};
  width: 100%;
  --content-max-width: 1280px;

  h2 {
    color: ${({ $text_color }) => $text_color};
  }
`

const DisclaimerTextContainer = styled(Row)`
  max-width: 43.75rem;

  * {
    font-size: ${calcRem(theme.font.static.body.regular.compact.sm.fontSize)}rem;
  }
`

const Container = styled(Stack)<{
  $itemCount: number
  $desktopColumns?: number
  'data-has-desktop-columns': boolean
}>`
  display: grid;
  justify-items: center;
  gap: 2rem 1.5rem;

  @media ${screenSizes.tablet} {
    grid-template-columns: ${({ $itemCount }) =>
      $itemCount % 2 === 0 ? '1fr 1fr' : `repeat(${$itemCount}, 1fr)`};
    max-width: 1440px;
    padding: ${spacing(0, 'lg')};
  }

  @media ${screenSizes.smDesktop} {
    /* using a data attribute allows for cleaner logic here
       even though we have access to $desktopColumns */
    &[data-has-desktop-columns='true'] {
      grid-template-columns: ${({ $desktopColumns }) => `repeat(${$desktopColumns}, 1fr)`};
    }
  }
`

const RefreshContainer = styled(Stack)<{ $desktopColumns?: number }>`
  gap: 24px;
  display: flex;
  align-items: start;

  @media ${screenSizes.tablet} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${screenSizes.smDesktop} {
    grid-template-columns: ${({ $desktopColumns }) =>
      $desktopColumns ? `repeat(${$desktopColumns}, 1fr)` : 'repeat(3, 1fr)'};
    gap: 32px;
  }

  @media ${screenSizes.max_mobile} {
    padding-bottom: 1rem;
  }
`

const HeadingContainer = styled(Column)`
  width: 100%;

  @supports (text-wrap: pretty) {
    text-wrap: pretty;
  }

  @supports not (text-wrap: pretty) {
    white-space: normal;
  }
`

const MultiStoryLink = styled(ButtonLink)`
  margin: ${spacing('xxl', 'auto', 0)};
  text-decoration: none;
`

const CarouselWrapper = styled(Box)`
  position: relative;

  .with-background {
    .swiper-pagination-bullet {
      background-color: ${theme.gray600};
      opacity: 1;
    }

    .swiper-pagination-bullet-active.swiper-pagination-bullet {
      background-color: ${theme.gray300};
      opacity: 1;
    }
  }

  margin: 0 -1rem;
`
